import { Trash, PencilSimple, Megaphone, Coins, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useState } from "react";
import { inr } from "../../lib/format";
import NearbyMarketCard from "../NearbyMarketCard";
import CashflowLedger from "../networth/CashflowLedger";
import { Stat } from "./shared";

/**
 * One property in the grid. Pure presentation; all actions are passed in
 * by the parent so this component owns no state.
 */
export default function PropertyCard({ p, onEdit, onDelete, onListForRent }) {
  const [showCashflow, setShowCashflow] = useState(false);
  return (
    <div className="card-flat p-6" data-testid={`property-card-${p.id}`}>
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <div className="eyebrow">{p.type}</div>
            <span
              className={`text-[0.6rem] tracking-widest uppercase px-1.5 py-0.5 border ${
                p.status === "owned"
                  ? "border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]"
                  : p.status === "sold"
                    ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                    : "hairline text-muted-foreground"
              }`}
              data-testid={`status-badge-${p.id}`}
            >
              {p.status === "owned" ? "Owned" : p.status === "sold" ? "Sold" : "Evaluating"}
            </span>
          </div>
          <div className="font-serif text-2xl mt-1">{p.name}</div>
          <div className="text-xs text-muted-foreground">{p.location}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(p)} className="btn-ghost p-2" title="Edit" data-testid={`edit-${p.id}`}>
            <PencilSimple size={14} />
          </button>
          <button onClick={() => onDelete(p.id)} className="btn-ghost p-2" title="Delete" data-testid={`delete-${p.id}`}>
            <Trash size={14} />
          </button>
        </div>
      </div>

      <div className="num-metric text-3xl mt-4">{inr(p.price)}</div>
      <div className="text-xs text-muted-foreground mt-1">
        {p.area_sqft} sqft · ₹{Math.round(p.price / (p.area_sqft || 1)).toLocaleString("en-IN")}/sqft
      </div>

      {p.status === "owned" && p.current_value ? (
        <div className="mt-3 text-xs text-[hsl(var(--secondary))]">
          Now worth {inr(p.current_value)}
          {p.purchase_price ? ` · ${formatPct(p.current_value, p.purchase_price)}` : ""}
        </div>
      ) : null}

      {p.status === "sold" && p.sold_price ? <SoldSummary p={p} /> : null}

      <div className="mt-4 pt-4 border-t hairline grid grid-cols-3 gap-2 text-xs">
        <Stat label="Loan" value={`${p.loan_rate}%`} />
        <Stat label="Tenure" value={`${p.loan_tenure_years}y`} />
        <Stat label="Appr." value={`${p.expected_appreciation}%`} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {[
          ["Loc", p.score_location],
          ["Amen", p.score_amenities],
          ["Safe", p.score_safety],
          ["Comm", p.score_commute],
          ["Resale", p.score_resale],
        ].map(([l, v]) => (
          <span key={l} className="text-xs px-2 py-1 border hairline text-muted-foreground">
            {l} · <span className="text-foreground">{v}</span>
          </span>
        ))}
      </div>

      {p.status === "owned" && (
        <button
          onClick={(e) => { e.stopPropagation(); onListForRent(p); }}
          className="btn-ghost w-full mt-3 py-2 text-xs inline-flex items-center justify-center gap-2 border border-dashed border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]"
          data-testid={`list-for-rent-${p.id}`}
        >
          <Megaphone size={12} weight="duotone" /> List for rent in 1 click
        </button>
      )}
      {p.status === "owned" && (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowCashflow((v) => !v)}
            className="w-full mt-3 py-2 text-xs inline-flex items-center justify-center gap-2 border hairline hover:bg-muted/30"
            data-testid={`cashflow-toggle-${p.id}`}
          >
            <Coins size={12} weight="duotone" />
            {showCashflow ? "Hide cashflow ledger" : "Add cashflows & True XIRR"}
            {showCashflow ? <CaretUp size={10} /> : <CaretDown size={10} />}
          </button>
          {showCashflow && <CashflowLedger propertyId={p.id} propertyName={p.name} />}
        </div>
      )}
      <div onClick={(e) => e.stopPropagation()}>
        <NearbyMarketCard property={p} />
      </div>
    </div>
  );
}

function formatPct(current, base) {
  if (!base) return "";
  const pct = ((current - base) / base) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
}

function SoldSummary({ p }) {
  const base = p.purchase_price || p.price || 0;
  const gain = p.sold_price - base;
  const pct = base ? (gain / base) * 100 : 0;
  const positive = gain >= 0;
  const dateStr = p.sold_date
    ? ` · ${new Date(p.sold_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}`
    : "";
  return (
    <div className="mt-3 text-xs" data-testid={`sold-summary-${p.id}`}>
      <span className={positive ? "text-[hsl(var(--secondary))]" : "text-destructive"}>
        Sold for {inr(p.sold_price)} · {positive ? "+" : ""}
        {inr(gain)} ({positive ? "+" : ""}
        {pct.toFixed(1)}%){dateStr}
      </span>
    </div>
  );
}
